import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Shield, HeadphonesIcon, Award, Zap, Settings, FileCheck, GraduationCap } from 'lucide-react';
import { Link } from 'react-router-dom';

const Solutions = () => {
  const { t } = useTranslation();

  const solutions = [
    {
      icon: Shield,
      title: t('solutions.qualityTitle'),
      description: t('solutions.qualityDesc'),
      features: [t('sol.features.iso'), t('sol.features.gxp'), t('sol.features.doc'), t('sol.features.reg')],
      image: 'https://images.unsplash.com/photo-1669101283985-ab2b293923df?w=600&q=80'
    },
    {
      icon: HeadphonesIcon,
      title: t('solutions.supportTitle'),
      description: t('solutions.supportDesc'),
      features: [t('sol.features.t247'), t('sol.features.training'), t('sol.features.install'), t('sol.features.maint')],
      image: 'https://images.pexels.com/photos/6471911/pexels-photo-6471911.jpeg?auto=compress&cs=tinysrgb&w=600'
    },
    {
      icon: Award,
      title: t('solutions.customTitle'),
      description: t('solutions.customDesc'),
      features: [t('sol.features.needs'), t('sol.features.custom'), t('sol.features.sys'), t('sol.features.opt')],
      image: 'https://images.unsplash.com/photo-1748002645678-7f9c04263315?w=600&q=80'
    }
  ];

  const services = [
    {
      icon: Settings,
      title: t('sol.additional.cal.title'),
      description: t('sol.additional.cal.desc'),
      features: [t('sol.features.iso'), t('sol.additional.cal.fast'), t('sol.additional.cal.onsite'), t('sol.additional.cal.doc')]
    },
    {
      icon: GraduationCap,
      title: t('sol.additional.edu.title'),
      description: t('sol.additional.edu.desc'),
      features: [t('sol.additional.edu.ws'), t('sol.additional.edu.mod'), t('sol.additional.edu.cert'), t('sol.additional.edu.best')]
    },
    {
      icon: FileCheck,
      title: t('sol.additional.val.title'),
      description: t('sol.additional.val.desc'),
      features: [t('sol.additional.val.iq'), t('sol.additional.val.gxp'), t('sol.additional.val.audit'), t('sol.additional.val.reg')]
    },
    {
      icon: Zap,
      title: t('sol.additional.sys.title'),
      description: t('sol.additional.sys.desc'),
      features: [t('sol.additional.sys.data'), t('sol.additional.sys.cloud'), t('sol.additional.sys.api'), t('sol.additional.sys.soft')]
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Header */}
      <section className="bg-gradient-to-r from-green-900 to-green-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('solutions.title')}</h1>
            <p className="text-xl text-green-100">{t('solutions.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Main Solutions */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
                >
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="h-10 w-10 text-green-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{solution.title}</h2>
                    <p className="text-lg text-gray-600 mb-6">{solution.description}</p>
                    <ul className="space-y-3 mb-6">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild className="bg-green-600 hover:bg-green-700">
                      <Link to="/contact">{t('products.learnMore')}</Link>
                    </Button>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                    <Card className="overflow-hidden shadow-xl">
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-80 object-cover"
                      />
                    </Card>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('sol.additional.title')}</h2>
            <p className="text-lg text-gray-600">{t('sol.additional.subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-shadow duration-300">
                    <CardHeader>
                      <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="h-7 w-7 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{service.description}</p>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                            <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('sol.process.title')}</h2>
            <p className="text-lg text-gray-600">{t('sol.process.subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: t('sol.process.s1.title'), description: t('sol.process.s1.desc') },
              { step: '02', title: t('sol.process.s2.title'), description: t('sol.process.s2.desc') },
              { step: '03', title: t('sol.process.s3.title'), description: t('sol.process.s3.desc') },
              { step: '04', title: t('sol.process.s4.title'), description: t('sol.process.s4.desc') }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('sol.cta.title')}
            </h2>
            <p className="text-xl text-green-100 mb-8">
              {t('sol.cta.subtitle')}
            </p>
            <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              <Link to="/contact">{t('sol.cta.button')}</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;